import React, { useEffect, useMemo, useState } from "react";
import ApexoDrawer from "../common/apexo/ApexoDrawer";
import "../../styles/appShellLayout.css";

interface AppShellLayoutProps {
  left?: React.ReactNode;
  main: React.ReactNode;
  right?: React.ReactNode;
  header?: React.ReactNode;
  leftCollapsible?: boolean;
  rightCollapsible?: boolean;
  moduleId?: string; // For storing collapse state per module
}

function getViewportMode(width: number) {
  if (width <= 767) return "mobile";
  if (width <= 1199) return "compact";
  if (width <= 1366) return "laptop-small";
  if (width <= 1440) return "laptop-medium";
  if (width <= 1600) return "laptop-large";
  return "desktop";
}

// Custom hook for persisted state
function usePersistedState<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = (value: T) => {
    setState(value);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (err) {
        console.warn(`Failed to set localStorage key "${key}":`, err);
      }
    }
  };

  return [state, setValue];
}

export default function AppShellLayout({
  left,
  main,
  right,
  header,
  leftCollapsible = true,
  rightCollapsible = true,
  moduleId = "default"
}: AppShellLayoutProps) {
  const [viewport, setViewport] = useState(() => {
    if (typeof window === "undefined") return "desktop";
    return getViewportMode(window.innerWidth);
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persisted collapse states
  const [leftCollapsed, setLeftCollapsed] = usePersistedState(`app-left-collapsed`, false);
  const [rightCollapsed, setRightCollapsed] = usePersistedState(`app-right-collapsed-${moduleId}`, false);

  useEffect(() => {
    const handleResize = () => {
      const next = getViewportMode(window.innerWidth);
      setViewport(next);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isCompact = viewport === "compact" || viewport === "mobile";
  const showRightInGrid = right && !isCompact && !rightCollapsed;
  const showDrawerButton = Boolean(right) && isCompact;

  useEffect(() => {
    if (!isCompact) {
      setDrawerOpen(false);
    }
  }, [isCompact]);

  const leftClassName = useMemo(() => {
    const classes = ["app-shell-left"];
    if (isCompact || leftCollapsed) classes.push("collapsed");
    if (!left) classes.push("empty");
    return classes.join(" ");
  }, [isCompact, left, leftCollapsed]);

  const bodyClassName = useMemo(() => {
    const classes = ["app-shell-body"];
    if (!left) classes.push("no-left");
    if (leftCollapsed && !isCompact) classes.push("left-collapsed");
    if (rightCollapsed && right && !isCompact) classes.push("right-collapsed");
    if (leftCollapsed && rightCollapsed && right && !isCompact) classes.push("both-collapsed");
    if (!right) classes.push("no-right");
    return classes.join(" ");
  }, [leftCollapsed, rightCollapsed, right, isCompact, left]);

  return (
    <div className="app-shell">
      {header && (
        <div className="app-shell-header">
          {header}
        </div>
      )}
      <div className={bodyClassName}>
        {left && (
          <div className={leftClassName}>
            {leftCollapsible && !isCompact && (
              <button
                className="panel-toggle-btn left-toggle"
                onClick={() => setLeftCollapsed(!leftCollapsed)}
                title={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-label={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {leftCollapsed ? "→" : "←"}
              </button>
            )}
            {left}
          </div>
        )}
        <div className="app-shell-main">{main}</div>
        {showRightInGrid && (
          <div className="app-shell-right">
            {rightCollapsible && (
              <button
                className="panel-toggle-btn right-toggle"
                onClick={() => setRightCollapsed(!rightCollapsed)}
                title="Close panel"
                aria-label="Close panel"
              >
                ✕
              </button>
            )}
            {right}
          </div>
        )}
        {rightCollapsed && right && !isCompact && (
          <button
            className="right-panel-expand-btn"
            onClick={() => setRightCollapsed(false)}
            title="Open info panel"
            aria-label="Open info panel"
          >
            ☰
          </button>
        )}
      </div>

      {showDrawerButton && (
        <button
          type="button"
          className="app-shell-drawer-btn"
          onClick={() => setDrawerOpen(true)}
        >
          Details
        </button>
      )}

      {right && (
        <ApexoDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="Details"
          width="360px"
        >
          {right}
        </ApexoDrawer>
      )}
    </div>
  );
}
